import React, { useState, useEffect, useRef } from "react";
import { db } from "./firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
  getDocs,
  doc,
  setDoc,
  serverTimestamp
} from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import LiveChat from "./LiveChat";
import CryptoJS from "crypto-js";

const ENCRYPTION_KEY = process.env.REACT_APP_CHAT_ENCRYPTION_KEY || "nabi-secure-chat-key-default-2026";
const FALLBACK_KEY = "nabi-secure-chat-key-default-2026";

const decryptMessage = (cipherText) => {
  if (!cipherText || cipherText === "No messages yet") return cipherText;
  try {
    // 1. Try primary key
    let bytes = CryptoJS.AES.decrypt(cipherText, ENCRYPTION_KEY);
    let originalText = bytes.toString(CryptoJS.enc.Utf8);
    if (originalText) return originalText;

    // 2. Try fallback key if it's different (handles transition period)
    if (ENCRYPTION_KEY !== FALLBACK_KEY) {
      bytes = CryptoJS.AES.decrypt(cipherText, FALLBACK_KEY);
      originalText = bytes.toString(CryptoJS.enc.Utf8);
      if (originalText) return originalText;
    }

    return cipherText;
  } catch (err) {
    return cipherText;
  }
};

export const PACKAGE_CHAT_VALIDITY_DAYS = {
  1: 1,  // 24 hours for 1 session
  2: 28, // 4 weeks
  4: 56  // 8 weeks
};

const checkIsExpired = (status, expireDate, createdAt, sessionCount, item) => {
  if (status === "completed" || status === "expired") return true;
  
  if (item) {
    const totalSessions = parseInt(sessionCount) || 1;
    const validityDays = PACKAGE_CHAT_VALIDITY_DAYS[totalSessions] || PACKAGE_CHAT_VALIDITY_DAYS[1];

    let lastSessionData = item[`session_${totalSessions}`];
    if (!lastSessionData && totalSessions === 1) lastSessionData = item; // Fallback for root data

    if (!lastSessionData || !lastSessionData.date || lastSessionData.date === "TBD") {
      const bookingTime = createdAt?.toMillis ? createdAt.toMillis() : (createdAt ? new Date(createdAt).getTime() : Date.now());
      const expirationTime = bookingTime + validityDays * 24 * 60 * 60 * 1000;
      return Date.now() > expirationTime;
    }

    let expirationTime = 0;
    try {
        const timeParts = (lastSessionData.time || "").trim().split('-');
        const targetTime = timeParts[timeParts.length - 1]?.trim() || "";
        
        const dateParts = lastSessionData.date.split('-');
        let year, month, day;
        if (dateParts.length === 3) {
            if (dateParts[0].length === 4) {
            year = dateParts[0]; month = dateParts[1]; day = dateParts[2];
            } else {
            day = dateParts[0]; month = dateParts[1]; year = dateParts[2];
            }
            
            let [timeStr, modifier] = targetTime.split(' ');
            if (!modifier && timeParts.length > 1) {
            const firstPart = timeParts[0].trim();
            if (firstPart.toUpperCase().includes('AM')) modifier = 'AM';
            else if (firstPart.toUpperCase().includes('PM')) modifier = 'PM';
            }
            
            let [hours, minutes] = (timeStr || "0:0").split(':');
            hours = parseInt(hours, 10);
            minutes = parseInt(minutes, 10);
            if (modifier && modifier.toUpperCase() === 'PM' && hours < 12) hours += 12;
            if (modifier && modifier.toUpperCase() === 'AM' && hours === 12) hours = 0;
            
            const sessionDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), hours, minutes);
            if (!isNaN(sessionDate.getTime())) {
                expirationTime = sessionDate.getTime() + 24 * 60 * 60 * 1000;
            }
        }
    } catch (e) {
        console.error("Error parsing session date in checkIsExpired:", e);
    }

    if (expirationTime > 0) {
        return Date.now() > expirationTime;
    }

    // Fallback if parsing fails
    const bookingTime = createdAt?.toMillis ? createdAt.toMillis() : (createdAt ? new Date(createdAt).getTime() : Date.now());
    return Date.now() > (bookingTime + validityDays * 24 * 60 * 60 * 1000);
  }

  return false;
};

const getAvatarColor = (name) => {
  const colors = ['#003049', '#C57B57', '#F07167', '#003049', '#888', '#C57B57'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const formatChatTime = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toMillis ? new Date(timestamp.toMillis()) : new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  if (diff < 24 * 60 * 60 * 1000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diff < 48 * 60 * 60 * 1000) return "Yesterday";
  return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

export default function TherapistChatPopup({ user, onClose }) {
  const [conversations, setConversations] = useState([]);
  const [allConversations, setAllConversations] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [loading, setLoading] = useState(true);

  const selectedConvIdRef = useRef(selectedConvId);
  useEffect(() => {
    selectedConvIdRef.current = selectedConvId;
  }, [selectedConvId]);

  // Use refs to store latest data for building the merged list
  const sessionsMapRef = useRef({});
  const bookingsRef = useRef([]);

  useEffect(() => {
    const effectiveTid = user.therapistId || user.uid;
    const tids = [effectiveTid, user.uid, user.email].filter(Boolean);
    console.log("Therapist Chat TIDs:", tids);

    // Reset data when user changes
    sessionsMapRef.current = {};
    bookingsRef.current = [];


    // 1. Listen for existing active chat sessions
    const qSessions = query(
      collection(db, "chat_sessions"),
      where("therapistId", "in", tids)
    );

    const qSessionsByEmail = query(
      collection(db, "chat_sessions"),
      where("therapistEmail", "==", user.email)
    );

    // 3. Listen for all bookings for this therapist to populate the "contact list"
    const qBookingsById = query(
      collection(db, "bookings"),
      where("therapistId", "==", effectiveTid)
    );

    const qBookingsByEmail = query(
      collection(db, "bookings"),
      where("therapistEmail", "==", user.email)
    );

    const updateMergedList = () => {
      const bookings = bookingsRef.current;
      const sessionsMap = sessionsMapRef.current;

      console.log("[DEBUG] updateMergedList Called");
      console.log("[DEBUG] Total Bookings:", bookings.length, bookings.map(b => b.id));
      console.log("[DEBUG] Total Sessions in Map:", Object.keys(sessionsMap).length, sessionsMap);

      const merged = bookings.map(b => {
        // Try multiple session ID formats for backwards compatibility
        const sidNew = `chat_${b.userId}_${b.id}`;
        const sidOld = `chat_${b.userId}_${b.therapistId}_${b.id}`;
        
        let sessionData = {};
        let finalSessionId = sidNew;

        // Find all session documents that explicitly contain this bookingId
        const matchingSessions = Object.entries(sessionsMap).filter(([_, val]) => val.bookingId === b.id);
        let sessionEntry = null;

        if (matchingSessions.length > 0) {
          // Sort descending by updatedAt so the most recent session is picked
          matchingSessions.sort((a, b) => {
            const timeA = a[1].updatedAt?.toMillis ? a[1].updatedAt.toMillis() : 0;
            const timeB = b[1].updatedAt?.toMillis ? b[1].updatedAt.toMillis() : 0;
            return timeB - timeA;
          });
          sessionEntry = matchingSessions[0];
        }

        // Determine the best sessionId to use: Prefer the one with the most recent activity
        const sessionsToCompare = [];
        if (sessionEntry) sessionsToCompare.push({ id: sessionEntry[0], data: sessionEntry[1] });
        if (sessionsMap[sidNew] && sidNew !== (sessionEntry?.[0])) sessionsToCompare.push({ id: sidNew, data: sessionsMap[sidNew] });
        if (sessionsMap[sidOld] && sidOld !== (sessionEntry?.[0])) sessionsToCompare.push({ id: sidOld, data: sessionsMap[sidOld] });
        if (sessionsMap[b.id] && b.id !== (sessionEntry?.[0])) sessionsToCompare.push({ id: b.id, data: sessionsMap[b.id] });

        if (sessionsToCompare.length > 0) {
          sessionsToCompare.sort((a, b) => {
            if (a.id === selectedConvIdRef.current) return -1;
            if (b.id === selectedConvIdRef.current) return 1;

            const timeA = a.data.updatedAt?.toMillis ? a.data.updatedAt.toMillis() : 0;
            const timeB = b.data.updatedAt?.toMillis ? b.data.updatedAt.toMillis() : 0;
            return timeB - timeA;
          });
          sessionData = sessionsToCompare[0].data;
          finalSessionId = sessionsToCompare[0].id;
        }

        console.log(`[SESSION SELECTION] BookingId: ${b.id}, Patient: ${b.fullName}, Selected SessionId: ${finalSessionId}, Status: ${sessionData?.status || 'none'}, LastMsg: ${sessionData?.lastMessage ? 'Exists' : 'Empty'}`);

        return {
          id: finalSessionId,
          userId: b.userId,
          therapistId: b.therapistId,
          userName: b.fullName || b.userName || "Patient",
          bookingId: b.id,
          packageType: b.packageType || b.name,
          totalSessions: b.sessionCount || 1,
          sessionsUsed: b.sessionsUsed || 0,
          lastMessage: decryptMessage(sessionData.lastMessage) || "No messages yet",
          updatedAt: sessionData.updatedAt || null,
          bookingDate: b.createdAt || b.date || b.timestamp || sessionData.createdAt || null,
          expireDate: b.expireDate || b.expiryDate || b.endDate || null,
          isExpired: checkIsExpired(b.status, b.expireDate || b.expiryDate || b.endDate, b.createdAt || sessionData.createdAt, b.sessionCount || 1, b),
          status: sessionData.status || "active",
          isNew: !sessionData.lastMessage,
          unread: sessionData.unreadByTherapist || (sessionData.unreadCountByTherapist > 0),
          unreadCount: sessionData.unreadCountByTherapist || 0
        };
      });

      // Include all active chat sessions that DO NOT have a matching booking
      Object.entries(sessionsMap).forEach(([sessionId, sessionData]) => {
        // Skip if this session is already in the merged list
        const alreadyAdded = merged.some(m => 
          m.id === sessionId || 
          (sessionData.bookingId && m.bookingId === sessionData.bookingId)
        );

        if (!alreadyAdded) {
          merged.push({
            id: sessionId,
            userId: sessionData.userId || "unknown",
            therapistId: sessionData.therapistId || effectiveTid,
            userName: sessionData.userName || sessionData.patientName || "Patient",
            bookingId: sessionData.bookingId || null,
            packageType: sessionData.packageType || sessionData.category || "Direct Chat",
            totalSessions: sessionData.totalSessions || 1,
            sessionsUsed: sessionData.sessionsUsed || 0,
            lastMessage: decryptMessage(sessionData.lastMessage) || "No messages yet",
            updatedAt: sessionData.updatedAt || sessionData.createdAt || null,
            bookingDate: sessionData.createdAt || sessionData.updatedAt || null,
            expireDate: sessionData.expireDate || sessionData.expiryDate || sessionData.endDate || null,
            isExpired: checkIsExpired(sessionData.status, sessionData.expireDate || sessionData.expiryDate || sessionData.endDate, sessionData.createdAt, sessionData.totalSessions || 1, sessionData),
            status: sessionData.status || "active",
            isNew: !sessionData.lastMessage,
            unread: sessionData.unreadByTherapist || (sessionData.unreadCountByTherapist > 0),
            unreadCount: sessionData.unreadCountByTherapist || 0
          });
        }
      });

      // Deduplicate by userId so a patient only appears once in the inbox
      const uniqueUsersMap = new Map();
      
      const getTime = (val) => {
        if (!val) return 0;
        if (typeof val.toMillis === 'function') return val.toMillis();
        if (val instanceof Date) return val.getTime();
        if (typeof val === 'number') return val;
        if (val.seconds) return val.seconds * 1000;
        return new Date(val).getTime() || 0;
      };

      merged.forEach(item => {
        if (!item.userId || item.userId === "unknown") {
          uniqueUsersMap.set(item.id, item);
          return;
        }

        if (!uniqueUsersMap.has(item.userId)) {
          uniqueUsersMap.set(item.userId, item);
        } else {
          const existing = uniqueUsersMap.get(item.userId);
          const existingTime = getTime(existing.updatedAt);
          const newTime = getTime(item.updatedAt);
          
          // Chat Rollover: If the user has any active package, keep the chat active
          const mergedIsExpired = existing.isExpired && item.isExpired;
          
          // Collect all session IDs for this user so the therapist can switch between them
          const allUserSessions = existing.allSessions || [existing];
          if (!allUserSessions.some(s => s.id === item.id)) allUserSessions.push(item);

          // Priority: 1. Active session wins 2. Non-expired beats expired 3. Most recent activity wins.
          const shouldSwitch = item.id === selectedConvIdRef.current ||
                               (existing.id !== selectedConvIdRef.current && 
                               ((!item.isExpired && existing.isExpired) || 
                               (item.isExpired === existing.isExpired && newTime > existingTime)));
          
          const mergedUnread = item.unread || existing.unread;
          const mergedUnreadCount = (item.unreadCount || 0) + (existing.unreadCount || 0);

          if (shouldSwitch) {
            uniqueUsersMap.set(item.userId, {
              ...item,
              packageType: existing.packageType && existing.packageType !== item.packageType ? "Multiple Packages" : item.packageType,
              isExpired: mergedIsExpired,
              allSessions: allUserSessions,
              unread: mergedUnread,
              unreadCount: mergedUnreadCount
            });
          } else {
            uniqueUsersMap.set(item.userId, {
              ...existing,
              packageType: item.packageType && existing.packageType !== item.packageType ? "Multiple Packages" : existing.packageType,
              isExpired: mergedIsExpired,
              allSessions: allUserSessions,
              unread: mergedUnread,
              unreadCount: mergedUnreadCount
            });
          }
        }
      });
      
      const finalMerged = Array.from(uniqueUsersMap.values());
      
      // Filter out completely expired sessions from the UI
      // STRICT: We only show sessions that are NOT expired to match the user-side behavior.
      const activeConversations = finalMerged.filter(conv => !conv.isExpired);

      // Sort by updatedAt (newest first), then by name
      activeConversations.sort((a, b) => {
        const timeA = getTime(a.updatedAt);
        const timeB = getTime(b.updatedAt);

        if (timeB !== timeA) return timeB - timeA;
        return a.userName.localeCompare(b.userName);
      });

      setAllConversations(merged);
      setConversations(activeConversations);
      setLoading(false);
    };

    const unsubSessions = onSnapshot(qSessions, (snap) => {
      snap.docs.forEach(doc => {
        sessionsMapRef.current[doc.id] = { ...sessionsMapRef.current[doc.id], ...doc.data() };
      });
      updateMergedList();
    });

    const unsubSessionsEmail = onSnapshot(qSessionsByEmail, (snap) => {
      snap.docs.forEach(doc => {
        sessionsMapRef.current[doc.id] = { ...sessionsMapRef.current[doc.id], ...doc.data() };
      });
      updateMergedList();
    });

    const unsubBookingsId = onSnapshot(qBookingsById, (snap) => {
      const newBookings = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const updatedBookings = [...bookingsRef.current];

      newBookings.forEach(nb => {
        const idx = updatedBookings.findIndex(ob => ob.id === nb.id);
        if (idx === -1) updatedBookings.push(nb);
        else updatedBookings[idx] = nb;

        // Individual listeners for each possible session ID format
        const formats = [`chat_${nb.userId}_${nb.id}`, `chat_${nb.userId}_${nb.therapistId}_${nb.id}`, nb.id];
        formats.forEach(sid => {
          if (!sessionsMapRef.current[sid]) {
            const sRef = doc(db, "chat_sessions", sid);
            onSnapshot(sRef, (sSnap) => {
              if (sSnap.exists()) {
                sessionsMapRef.current[sid] = { ...sessionsMapRef.current[sid], ...sSnap.data() };
                updateMergedList();
              }
            });
          }
        });
      });
      bookingsRef.current = updatedBookings;
      updateMergedList();
    });

    const unsubBookingsEmail = onSnapshot(qBookingsByEmail, (snap) => {
      const newBookings = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const currentBookings = bookingsRef.current;
      const updatedBookings = [...currentBookings];

      newBookings.forEach(nb => {
        const idx = updatedBookings.findIndex(ob => ob.id === nb.id);
        if (idx === -1) {
          updatedBookings.push(nb);
        } else {
          updatedBookings[idx] = nb; // Update existing booking
        }
      });
      bookingsRef.current = updatedBookings;
      updateMergedList();
    });

    return () => {
      unsubSessions();
      unsubSessionsEmail();
      unsubBookingsId();
      unsubBookingsEmail();
    };
  }, [user.uid, user.email, user.therapistId]);

  // --- DATA HEALING ---
  // If a conversation says "No messages yet", try to fetch the actual last message from chat_messages
  const checkedIds = useRef(new Set());
  useEffect(() => {
    conversations.forEach(async (conv) => {
      if (conv.lastMessage === "No messages yet" && !checkedIds.current.has(conv.id)) {
        checkedIds.current.add(conv.id);
        console.log(`[HEAL DEBUG] Checking firestore for missing lastMessage in session: ${conv.id}`);
        try {
          // Avoid using orderBy and limit together with where() to prevent composite index errors
          const q = query(
            collection(db, "chat_messages"),
            where("sessionId", "==", conv.id)
          );
          const snap = await getDocs(q);
          if (!snap.empty) {
            // Sort client-side to find the most recent message
            const msgs = snap.docs.map(d => d.data());
            msgs.sort((a, b) => {
              const timeA = a.timestamp?.toMillis ? a.timestamp.toMillis() : 0;
              const timeB = b.timestamp?.toMillis ? b.timestamp.toMillis() : 0;
              return timeB - timeA;
            });
            const lastMsg = msgs[0];
            const healedText = decryptMessage(lastMsg.text);
            console.log(`[HEAL SUCCESS] Found message for ${conv.userName} (${conv.id}): "${healedText}"`);
            
            // Immediately update the local state to show it on the UI
            setConversations(prev => prev.map(c => 
              c.id === conv.id ? { 
                ...c, 
                lastMessage: decryptMessage(lastMsg.text), 
                updatedAt: lastMsg.timestamp || Date.now(),
                isNew: false 
              } : c
            ));

            // Update the session document so it persists for everyone
            await setDoc(doc(db, "chat_sessions", conv.id), {
              lastMessage: lastMsg.text,
              updatedAt: lastMsg.timestamp || serverTimestamp()
            }, { merge: true });
          } else {
            console.log(`[HEAL DEBUG] No messages found in firestore for ${conv.id}`);
          }
        } catch (err) {
          console.error("[HEAL DEBUG] Healing error for", conv.id, err);
        }
      }
    });
  }, [conversations]);

  // Set default selection once conversations are loaded
  useEffect(() => {
    if (conversations.length > 0 && !selectedConvId) {
      setSelectedConvId(conversations[0].id);
    }
  }, [conversations, selectedConvId]);

  return (
    <div style={styles.overlay}>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        style={styles.popupContainer}
      >
        <div style={styles.popupHeader}>
          <div style={styles.headerLeft}>
            <div style={styles.onlineDot} />
            <h3 style={styles.headerTitle}>Messages</h3>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        <div style={styles.splitView}>
          {/* LEFT SIDE: INBOX LIST */}
          <div style={styles.inboxSide}>
            {loading ? (
              <div style={styles.centerText}>Loading contacts...</div>
            ) : conversations.length === 0 ? (
              <div style={styles.centerText}>No patients found</div>
            ) : (
              conversations.map((conv) => {
                const isActiveUser = selectedConvId && 
                                     (selectedConvId === conv.id || 
                                     (conv.userId && conv.userId !== "unknown" && allConversations.find(c => c.id === selectedConvId)?.userId === conv.userId));
                return (
                <div
                  key={conv.id}
                  style={{
                    ...styles.convItem,
                    backgroundColor: isActiveUser ? '#F0F2F5' : 'transparent',
                  }}
                  onClick={() => setSelectedConvId(conv.id)}
                >
                  <div style={{
                    ...styles.convAvatar,
                    backgroundColor: getAvatarColor(conv.userName)
                  }}>
                    {conv.userName?.charAt(0) || "U"}
                  </div>
                  <div style={styles.convInfo}>
                    <div style={styles.convTop}>
                      <span style={styles.convName}>{conv.userName}</span>
                      <span style={styles.convTime}>
                        {conv.updatedAt ? formatChatTime(conv.updatedAt) : ""}
                      </span>
                    </div>
                    <div style={styles.convPreviewRow}>
                      <p style={{
                        ...styles.convPreview,
                        fontWeight: conv.unread ? 'bold' : 'normal',
                        color: conv.unread ? '#333' : '#666'
                      }}>{conv.lastMessage}</p>
                      {conv.unread && (
                        <div style={styles.unreadBadge}>
                          {conv.unreadCount > 0 ? conv.unreadCount : ""}
                        </div>
                      )}
                    </div>
                    {conv.packageType && (
                      <div style={styles.packageBadgeRow}>
                        <span style={styles.packageBadge}>{conv.packageType}</span>
                        <span style={styles.sessionBadge}>{conv.sessionsUsed}/{conv.totalSessions}</span>
                      </div>
                    )}
                  </div>
                </div>
                );
              })
            )}
          </div>

          <div style={styles.chatSide}>
            {(() => {
              const selectedConv = conversations.find(c => c.id === selectedConvId) || allConversations.find(c => c.id === selectedConvId);
              return selectedConv ? (
                <LiveChat
                  sessionId={selectedConvId}
                  currentUser={{ ...user, role: 'therapist' }}
                  patientName={selectedConv.userName}
                  onEnd={() => setSelectedConvId(null)}
                  userSessions={(selectedConv.allSessions || [selectedConv]).filter(s => !s.isExpired)}
                  onSessionSwitch={(newId) => {
                    setSelectedConvId(newId);
                    // Also update the main conversation object in state if needed
                    setConversations(prev => prev.map(c => 
                      c.userId === selectedConv.userId ? { ...c, id: newId } : c
                    ));
                  }}
                  initialData={{
                    userId: selectedConv.userId,
                    userName: selectedConv.userName,
                    therapistId: selectedConv.therapistId,
                    therapistName: user.displayName || user.name || "Therapist",
                    packageType: selectedConv.packageType,
                    totalSessions: selectedConv.totalSessions,
                    sessionsUsed: selectedConv.sessionsUsed
                  }}
                />
              ) : (
                <div style={styles.chatPlaceholder}>
                  <div style={{ fontSize: '40px' }}>💬</div>
                  <p>Select a patient to start chatting</p>
                </div>
              );
            })()}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 10000
  },
  popupContainer: {
    backgroundColor: '#FFF',
    width: '100%',
    maxWidth: '1200px',
    height: '80vh',
    borderRadius: '24px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    maxWidth: '95vw',
    maxHeight: '80vh'
  },
  popupHeader: {
    padding: '16px 24px',
    backgroundColor: '#003049',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  onlineDot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#4CAF50' },
  headerTitle: { margin: 0, fontSize: '18px', fontWeight: '600' },
  closeBtn: { background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' },
  splitView: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    backgroundColor: '#FFF7F1'
  },
  inboxSide: {
    width: '320px',
    borderRight: '1px solid #EEE',
    overflowY: 'auto',
    backgroundColor: '#FFF'
  },
  chatSide: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FFF7F1'
  },
  convItem: {
    padding: '16px 20px',
    display: 'flex',
    gap: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    borderBottom: '1px solid #F5F5F5',
    alignItems: 'center'
  },
  convAvatar: {
    width: '45px',
    height: '45px',
    borderRadius: '50%',
    backgroundColor: '#C57B57',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '700',
    flexShrink: 0
  },
  convInfo: { flex: 1, overflow: 'hidden' },
  convTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
  convName: { fontWeight: '700', fontSize: '15px', color: '#333' },
  convTime: { fontSize: '11px', color: '#999' },
  convPreviewRow: { display: 'flex', alignItems: 'center', gap: '4px' },
  convPreview: {
    margin: 0,
    fontSize: '13px',
    color: '#666',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1
  },
  unreadBadge: {
    backgroundColor: '#F07167',
    color: 'white',
    borderRadius: '50%',
    minWidth: '18px',
    height: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 'bold',
    padding: '0 4px'
  },
  packageBadgeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '4px',
    fontSize: '10px',
    fontWeight: '700',
    color: '#C57B57'
  },
  packageBadge: { color: '#C57B57' },
  sessionBadge: { color: '#C57B57' },
  centerText: { textAlign: 'center', padding: '40px', color: '#888' },
  chatPlaceholder: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#AAA',
    backgroundColor: '#FFF7F1'
  }
};
